import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, Loader2, ClipboardList, HelpCircle, Stethoscope, FlaskConical,
  Target, HeartPulse, CalendarCheck, Activity, Printer,
} from "lucide-react";

const Stage = ({ icon: Icon, title, children, done }) => (
  <div className="relative pl-12">
    <span className={`absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border-2 ${
      done ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"
    }`}>
      <Icon className="h-4 w-4" />
    </span>
    <h3 className="font-heading text-xl font-semibold text-foreground">{title}</h3>
    <div className="mt-2 text-sm leading-relaxed text-foreground">{children}</div>
  </div>
);

export default function JourneyPage() {
  const { role, caseId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => {
    api.getCase(caseId).then(setC).catch(() => navigate(`/${role}/cases`));
  }, [caseId]); // eslint-disable-line

  if (!c) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const fd = c.final_diagnosis || {};
  const cp = c.care_plan || {};
  const fu = c.follow_up || {};
  const timeline = c.prognosis_timeline || [];

  return (
    <div className="min-h-screen px-6 pb-28 pt-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center justify-between">
          <button data-testid="journey-back" onClick={() => navigate(`/${role}/cases/${caseId}`)}
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-4 w-4" /> Back to case
          </button>
          <Button variant="outline" onClick={() => navigate(`/${role}/cases/${caseId}/summary`)} className="gap-2">
            <Printer className="h-4 w-4" /> Print summary
          </Button>
        </div>

        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Patient journey</p>
          <h1 className="font-heading text-3xl font-semibold text-foreground sm:text-4xl">{c.patient?.name}</h1>
          <p className="text-sm text-muted-foreground">
            {[c.patient?.age && `${c.patient.age} yrs`, c.patient?.sex].filter(Boolean).join(" · ")}
          </p>
        </div>

        <Card className="p-8" data-testid="journey-sheet">
          <div className="flex flex-col gap-8 border-l-2 border-primary/20">
            <div className="-ml-[2px] flex flex-col gap-8">
              <Stage icon={ClipboardList} title="History" done>
                <p><b>Chief complaint:</b> {c.chief_complaint}</p>
                {c.hpi && <p className="mt-1">{c.hpi}</p>}
              </Stage>

              {c.answers?.some((a) => a.answer) && (
                <Stage icon={HelpCircle} title="Clarifying Questions" done>
                  <ul className="flex flex-col gap-1">
                    {c.answers.filter((a) => a.answer).map((a, i) => (
                      <li key={i}><b>{a.question}</b> — {a.answer}</li>
                    ))}
                  </ul>
                </Stage>
              )}

              {c.provisional_diagnoses?.length > 0 && (
                <Stage icon={Stethoscope} title="Provisional Differential" done>
                  {c.impression && <p className="mb-1 italic text-muted-foreground">{c.impression}</p>}
                  <ul className="flex flex-col gap-1">
                    {c.provisional_diagnoses.map((d, i) => (
                      <li key={i}>• {d.name} <Badge variant="secondary">{d.probability}%</Badge></li>
                    ))}
                  </ul>
                </Stage>
              )}

              {c.investigations?.length > 0 && (
                <Stage icon={FlaskConical} title="Investigations & Results" done>
                  <ul className="flex flex-col gap-1">
                    {c.investigations.map((inv, i) => (
                      <li key={i}>• <b>{inv.name}:</b> {c.investigation_results?.[i]?.result || "—"}</li>
                    ))}
                  </ul>
                </Stage>
              )}

              {fd.name && (
                <Stage icon={Target} title="Final Diagnosis" done>
                  <p className="font-semibold text-primary">{fd.name}</p>
                  {fd.stage && fd.stage !== "N/A" && <p><b>Stage:</b> {fd.stage}</p>}
                  {fd.confidence && <Badge className="mt-1">{fd.confidence} confidence</Badge>}
                </Stage>
              )}

              {cp.treatments?.length > 0 && (
                <Stage icon={HeartPulse} title="Care Plan & Prognosis" done>
                  <ul className="flex flex-col gap-1">
                    {cp.treatments.map((t, i) => <li key={i}>• <b>{t.modality}:</b> {t.detail}</li>)}
                  </ul>
                  {cp.prognosis?.outlook && <p className="mt-2"><b>Prognosis:</b> {cp.prognosis.outlook}</p>}
                </Stage>
              )}

              {fu.schedule?.length > 0 && (
                <Stage icon={CalendarCheck} title="Follow-up Plan" done>
                  <ul className="flex flex-col gap-1">
                    {fu.schedule.map((s, i) => <li key={i}>• <b>{s.timepoint}:</b> {s.action}</li>)}
                  </ul>
                </Stage>
              )}

              {timeline.length > 0 && (
                <Stage icon={Activity} title="Visits & Prognosis Over Time" done>
                  <div className="flex flex-col gap-2">
                    {timeline.map((p, i) => (
                      <div key={i} className="rounded-md border border-border p-3">
                        <div className="flex items-center gap-2">
                          <b>{p.date}</b>
                          {p.status && <Badge variant="secondary">{p.status}</Badge>}
                        </div>
                        <p className="mt-1 text-muted-foreground">{p.outlook}</p>
                      </div>
                    ))}
                  </div>
                </Stage>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
